import React, { useState, useEffect } from "react";
import { useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import { PaymentDropdown } from "components/common";
import sentIcon from "assets/sent.svg";
import { track } from "utils/analytics";
import formatCurrency from "utils/formatCurrency";

const uuidv4 = require("uuid/v4");

const defaultErrorMessage = `Please check to make sure you have enough funds in your account and try again. If the issue persists, please contact us .`;
const redeemPoints = async (
  user,
  paymentMethod,
  amount,
  processRedemption,
  setIsDone,
  setErrorMessage,
  idempotencyKey,
) => {
  // process Payment
  console.log("processing redemption");
  processRedemption({
    variables: {
      payment_method: paymentMethod,
      amount: parseFloat(amount),
      idempotencyKey,
    },
    update: (cache, { data }) => {
      if (data.processRedemption.status !== "OK") { return; }
      cache.writeData({
        data: {
          user: {
            __typename: "User",
            rewards: {
              __typename: "UserRewards",
              available_points: user.rewards.available_points - amount,
            },
          },
        },
      });
    },
    onCompleted: (data) => {},
  })
    .then((res) => {
      console.log("COMPLETED");
      if (res.data && res.data.processRedemption.status !== "OK") {
        const { display_message, message, status, code } = res.data.processRedemption;
        const error = new Error(message);
        error.graphQLErrors = [{ message, displayMessage: display_message, status, code }];
        throw error;
      }
      setIsDone(true);
      return { status: "OK" };
    })
    .catch((error) => {
      const { graphQLErrors = [] } = error;
      const errorMessage = graphQLErrors[0]?.displayMessage || defaultErrorMessage;
      setErrorMessage(errorMessage);
      return { status: "ERROR", message: error.message };
    });
  return { status: "OK" };
};
const handleError = (err) => {
  // Handle Payment console.error();
  console.log("ERROR");
};

const redeemPage = ({
  user,
  amount,
  points,
  closeDrawer,
  errorMessage,
  setErrorMessage,
  setIsDone,
  processRedemption,
  paymentMethod,
  setPaymentMethod,
  idempotencyKey,
  processRedemptionLoading,
}) => {

  return (
    <div className="flex flex-col p-3 text-center">
      <div className="w-full mb-10 text-2xl">Redeem your points</div>
      <div className="w-full font-bold">You'll receive:</div>
      <div className="w-full text-4xl text-green-primary p-5">{formatCurrency(amount)}</div>
      <div className="w-full font-bold">Dollars</div>
      <div className="w-full">
        Which account would you like us to send your money to?
      </div>
      <div className="w-full p-5 mb-5 mt-5">
        {/*
        renderSelect(user, setPaymentMethod)
      */}
        <PaymentDropdown
          paymentMethods={user.payment_methods}
          includeStatementCredit
          excludeBankAccounts
          disabled
          didSelect={(p) => {
            setPaymentMethod(p);
          }}
        />
      </div>
      <button
        type="button"
        className="w-full p-5 bg-green-primary text-white text-2xl font-bold"
        disabled={processRedemptionLoading}
        onClick={(e) => {
          // get paymentMethod
          let paymMethod;
          let adjustedAmount;
          if (paymentMethod) {
            paymMethod = paymentMethod;
            adjustedAmount = amount;
          } else {
            paymMethod = "LOAN-CREDIT";
            adjustedAmount = points;
          }
          redeemPoints(
            user,
            paymMethod,
            adjustedAmount,
            processRedemption,
            setIsDone,
            setErrorMessage,
            idempotencyKey,
          )
            .then((res) => {
              track("DASHBOARD_POINTS_REDEMPTION_SUCCESS", {
                numPoints: points,
              });
            })
            .catch((err) => {
              track("DASHBOARD_POINTS_REDEMPTION_FAILURE", {
                numPoints: points,
                message: err.message,
              });
              setErrorMessage(err.message);
              handleError(err);
            });
        }}
      >
        Submit
      </button>
    </div>
  );
};

const successPage = ({ amount, account, closeDrawer }) => {
  console.log("ACCOUNT IS:", account);
  const msg =
    account.type === "LOAN-CREDIT"
      ? "as statement credit"
      : `in the account ending in **${account.account_number_last_four}`;
  return (
    <div className="flex flex-col p-3 text-center">
      <div className="w-full mb-4 text-xl font-bold text-gray-primary">
        Awesome!
      </div>
      <div className="w-full mb-6 text-2xl">You're a little richer</div>
      <img src={sentIcon} alt="sent" className="mb-6" />
      <div className="w-full mb-6">
        Your <span className="font-bold">{formatCurrency(amount)}</span>
        {` has been sent. It should show up as ${msg} within 1-2 business days.`}
      </div>
      <button
        type="button"
        className="w-full p-5 bg-black text-white text-2xl font-bold"
        onClick={closeDrawer}
      >
        Close
      </button>
    </div>
  );
};

const errorPage = ({ errorMessage, setErrorMessage }) => (
  <div className="flex flex-col p-3 text-center">
    <div className="w-full mb-10 text-2xl">Payment Error</div>
    <div className="w-full mb-10 text-2xl">Oops...</div>
    <div className="w-full pb-8">
      { errorMessage }
    </div>
    <button
      type="button"
      className="w-full p-5 bg-black text-white text-2xl font-bold"
      onClick={(e) => {
        setErrorMessage(null);
      }}
    >
      Try Again
    </button>
  </div>
);

const RedeemPoints = ({ user, closeDrawer, amount, points }) => {
  useEffect(() => {
    track("DASHBOARD_OPENED_POINTS_REDEMPTION_SCREEN");
  }, []);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isDone, setIsDone] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [idempotencyKey] = useState(uuidv4());
  const [processRedemption, { loading: processRedemptionLoading }] = useMutation(PROCESS_REDEMPTION);

  let account;
  if (paymentMethod && paymentMethod.type !== "Statement Credit") {
    account = isDone
      ? user.payment_methods.filter((pm) => pm.id === paymentMethod)[0]
      : {};
  } else {
    account = { type: "LOAN-CREDIT" };
  }
  return (
    <div className="flex flex-col p-4 text-center sm:p-10">
      {errorMessage
        ? errorPage({ errorMessage, setErrorMessage })
        : isDone
          ? successPage({ amount, account, closeDrawer })
          : redeemPage({
            user,
            amount,
            points,
            closeDrawer,
            errorMessage,
            setErrorMessage,
            setIsDone,
            processRedemption,
            paymentMethod,
            setPaymentMethod,
            idempotencyKey,
            processRedemptionLoading,
          })}
    </div>
  );
};

const PROCESS_REDEMPTION = gql`
  mutation ProcessRedemption(
    $payment_method: String!
    $amount: Float!
    $idempotencyKey: String!
  ) {
    processRedemption(
      payment_method: $payment_method
      amount: $amount
      idempotencyKey: $idempotencyKey
    ) {
      status
      code
      message
      display_message
    }
  }
`;

export default RedeemPoints;
